import React, { useEffect, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import {
  EMPTY_RANGE,
  EXTRA_FILTERS,
  EXTRA_FILTER_BY_KEY,
  isExtraFilterActive,
} from "../../constants/extraFilters";
import "./ExtraFilters.css";

function RemoveButton({ label, onClick }) {
  return (
    <button
      type="button"
      className="ojx-chip__remove"
      aria-label={`Убрать фильтр «${label}»`}
      onClick={onClick}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path
          d="M1.5 1.5l7 7M8.5 1.5l-7 7"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

function AmountInput({ value, placeholder, onChange }) {
  return (
    <IMaskInput
      className="ojx-chip__input"
      mask={Number}
      scale={0}
      thousandsSeparator=" "
      min={0}
      unmask
      value={value == null ? "" : String(value)}
      onAccept={(next) => onChange(next === "" ? null : Number(next))}
      placeholder={placeholder}
    />
  );
}

/**
 * Кнопка «Добавить фильтр» и добавленные через неё фильтры.
 * value — { [key]: string | { from, to } } в порядке добавления.
 */
function ExtraFilters({ value, onChange }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [focusKey, setFocusKey] = useState(null);
  const menuRef = useRef(null);
  const chipRefs = useRef({});

  const addedKeys = Object.keys(value);
  const availableFilters = EXTRA_FILTERS.filter((filter) => !addedKeys.includes(filter.key));

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setIsMenuOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!focusKey) return;
    chipRefs.current[focusKey]?.querySelector("select, input")?.focus();
    setFocusKey(null);
  }, [focusKey]);

  const addFilter = (key) => {
    const initial = EXTRA_FILTER_BY_KEY[key].type === "range" ? EMPTY_RANGE : "";
    onChange({ ...value, [key]: initial });
    setIsMenuOpen(false);
    setFocusKey(key);
  };

  const updateFilter = (key, next) => onChange({ ...value, [key]: next });

  const removeFilter = (key) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  return (
    <>
      {addedKeys.map((key) => {
        const filter = EXTRA_FILTER_BY_KEY[key];
        const filterValue = value[key];
        const isActive = isExtraFilterActive(key, filterValue);

        return (
          <div
            key={key}
            ref={(node) => {
              chipRefs.current[key] = node;
            }}
            className={`ojx-chip${isActive ? " is-active" : ""}`}
          >
            <span className="ojx-chip__label">{filter.label}:</span>
            {filter.type === "range" ? (
              <span className="ojx-chip__range">
                <AmountInput
                  value={filterValue.from}
                  placeholder="от"
                  onChange={(from) => updateFilter(key, { ...filterValue, from })}
                />
                <span className="ojx-chip__dash">—</span>
                <AmountInput
                  value={filterValue.to}
                  placeholder="до"
                  onChange={(to) => updateFilter(key, { ...filterValue, to })}
                />
                <span className="ojx-chip__unit">₸</span>
              </span>
            ) : (
              <select
                className="ojx-chip__select"
                value={filterValue}
                onChange={(event) => updateFilter(key, event.target.value)}
              >
                <option value="">Все</option>
                {filter.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
            <RemoveButton label={filter.label} onClick={() => removeFilter(key)} />
          </div>
        );
      })}

      <div className="ojx-add" ref={menuRef}>
        <button
          type="button"
          className={`oj-add-filter${isMenuOpen ? " is-open" : ""}`}
          disabled={!availableFilters.length}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((prev) => !prev)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M2.667 4H13.333M4.667 8H11.333M6.667 12H9.333"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          Добавить фильтр
        </button>

        {isMenuOpen && (
          <div className="ojx-menu" role="menu">
            <div className="ojx-menu__title">Фильтровать по</div>
            {availableFilters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                role="menuitem"
                className="ojx-menu__item"
                onClick={() => addFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default ExtraFilters;
